import React, {
    useEffect,
    useMemo,
    useState,
    useCallback,
    useRef,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Plus,
    RefreshCw,
    Search,
    Filter,
    Download,
    Package,
    Mail,
    FileText,
    Archive,
    FileDown,
    Files,
} from "lucide-react";
import { toast } from "sonner";

import DataTable from "../components/DataTable";
import { openSigortaMailCompose } from "../lib/sigortaMailto";
import EntityForm from "../components/EntityForm";
import EntityMerkezBanner from "../components/EntityMerkezBanner";
import VekaletMerkezPanel, { VekaletUyariBanner, OcrDurumBadge } from "../components/VekaletMerkezPanel";
import ConfirmDialog from "../components/ConfirmDialog";
import EvrakSection from "../components/EvrakSection";
import EvrakFormModal from "../components/EvrakFormModal";
import AraciDetailDrawer from "../components/AraciDetailDrawer";
import DegerKaybiFormModal from "../components/DegerKaybiFormModal";
import SigortaBasvuruFormModal from "../components/SigortaBasvuruFormModal";
import SliderFormModal from "../components/SliderFormModal";
import VekaletDosyaSection from "../components/VekaletDosyaSection";
import OdemeMasrafSection from "../components/OdemeMasrafSection";
import OdemeTakipPanel from "../components/OdemeTakipPanel";
import MuvekkilTakipPanel from "../components/MuvekkilTakipPanel";
import SigortaDosyaTakipPanel from "../components/SigortaDosyaTakipPanel";
import SigortaDosyaDetayDrawer from "../components/SigortaDosyaDetayDrawer";
import MuvekkilDetayDrawer from "../components/MuvekkilDetayDrawer";
import AraciTakipPanel from "../components/AraciTakipPanel";
import DegerKaybiMerkezPanel from "../components/deger-kaybi/DegerKaybiMerkezPanel";
import DegerKaybiOgrenmeModal from "../components/deger-kaybi/DegerKaybiOgrenmeModal";
import DegerKaybiRowActions from "../components/deger-kaybi/DegerKaybiRowActions";
import DosyaNotHucre from "../components/DosyaNotHucre";
import SigortaTahkimGecisHucre from "../components/SigortaTahkimGecisHucre";

import { getEntity } from "../lib/entities";
import { getTableConfig } from "../lib/entityTableConfig";
import { crud, absoluteUrl, triggerDownload, whatsappMerkez } from "../lib/api";
import { degerKaybiMerkez } from "../lib/degerKaybiApi";
import { normalizeEntityPayload } from "../lib/panelPayloadNormalize";
import { EVRAK_PARENTS } from "../lib/evrakParent";
import { getListFilters } from "../lib/listFilters";
import ListFiltersPanel from "../components/ListFiltersPanel";
import BulkActionBar, { supportsBulkUpdate } from "../components/BulkActionBar";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const EVRAK_FORBIDDEN = new Set(["deger-kaybi-parcalari"]);

export default function EntityListPage({ entityKeyOverride, embedded = false }) {
    const { entityKey: routeEntityKey } = useParams();
    const entityKey = entityKeyOverride || routeEntityKey;
    const location = useLocation();
    const navigate = useNavigate();
    const entity = useMemo(() => getEntity(entityKey), [entityKey]);
    const tableConfig = useMemo(
        () => getTableConfig(entityKey, entity),
        [entityKey, entity],
    );

    const [rows, setRows] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [exportBusy, setExportBusy] = useState(false);
    const [search, setSearch] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [sortKey, setSortKey] = useState(null);
    const [sortDir, setSortDir] = useState("asc");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [formOpen, setFormOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [editIntent, setEditIntent] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [busy, setBusy] = useState(false);


    const [preparingId, setPreparingId] = useState(null);
    const [mailBusyId, setMailBusyId] = useState(null);
    const [araciDetail, setAraciDetail] = useState(null);
    const [pdfBusyId, setPdfBusyId] = useState(null);
    const [bulkPdfBusy, setBulkPdfBusy] = useState(false);
    const [calcPdfBusyId, setCalcPdfBusyId] = useState(null);
    const [dkWaBusyId, setDkWaBusyId] = useState(null);
    const [dkOgrenmeRow, setDkOgrenmeRow] = useState(null);
    const [dkOgrenmeBusy, setDkOgrenmeBusy] = useState(false);

    const [filterDraft, setFilterDraft] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({});
    const [odemePeriod, setOdemePeriod] = useState({
        yil: new Date().getFullYear(),
        ay: 0,
    });
    const [muvekkilPeriod, setMuvekkilPeriod] = useState({
        yil: new Date().getFullYear(),
        ay: 0,
    });
    const [sigortaPeriod, setSigortaPeriod] = useState({
        yil: new Date().getFullYear(),
        ay: 0,
    });
    const [sigortaKpiFilter, setSigortaKpiFilter] = useState(null);
    const [muvekkilKpiFilter, setMuvekkilKpiFilter] = useState(null);
    const [sigortaDetay, setSigortaDetay] = useState(null);
    const [dosyaNotDrawerDosyaId, setDosyaNotDrawerDosyaId] = useState(null);
    const [muvekkilDetay, setMuvekkilDetay] = useState(null);
    const [araciPeriod, setAraciPeriod] = useState({
        yil: new Date().getFullYear(),
        ay: 0,
    });
    const [vekaletArsiv, setVekaletArsiv] = useState(
        () => new URLSearchParams(location.search).get("arsiv") === "1",
    );

    const evrakRef = useRef(null);
    const vekaletDosyaRef = useRef(null);
    const odemeMasrafRef = useRef(null);

    const listFilters = useMemo(
        () => getListFilters(entityKey),
        [entityKey],
    );

    const dkFilterParams = useMemo(() => {
        const p = { ...appliedFilters };
        if (appliedSearch) p.search = appliedSearch;
        return p;
    }, [appliedFilters, appliedSearch]);

    const handleDkFilterPatch = useCallback((patch) => {
        setPage(1);
        setFilterDraft((prev) => {
            const next = { ...prev, ...patch };
            Object.keys(patch).forEach((k) => {
                if (patch[k] === "" || patch[k] == null) delete next[k];
            });
            setAppliedFilters(next);
            return next;
        });
    }, []);

    const evrakConfig =
        !EVRAK_FORBIDDEN.has(entityKey) && EVRAK_PARENTS[entityKey];

    const evrakParentId = useMemo(() => {
        if (!evrakConfig) return null;
        return evrakConfig.resolveParentId?.(editing) ?? null;
    }, [evrakConfig, editing]);

    const isAracilar = entityKey === "aracilar";
    const isDegerKaybiMod = entityKey === "deger-kaybi";
    const isSigortaBasvuru = entityKey === "sigorta-basvurulari";
    const isSlider = entityKey === "slider";
    const isSigortaDosya = entityKey === "sigorta-dosyalari";
    const isEvraklar = entityKey === "evraklar";
    const isVekaletler = entityKey === "vekaletler";
    const isOdemeler = entityKey === "odemeler";
    const isMuvekkiller = entityKey === "muvekkiller";
    const isReadOnlyCrud = Boolean(entity?.readOnlyCrud);

    const [odemeFormPreview, setOdemeFormPreview] = useState(null);
    const [odemeComputed, setOdemeComputed] = useState(null);

    const reloadEditingVekalet = useCallback(
        async (vekaletId) => {
            if (!vekaletId || !isVekaletler) return;
            try {
                const full = await crud.retrieve("vekaletler", vekaletId);
                setEditing(full);
            } catch {
                /* form alanları güncellenemezse sessiz kal */
            }
        },
        [isVekaletler],
    );

    const reloadEditingOdeme = useCallback(
        async (odemeId) => {
            if (!odemeId || !isOdemeler) return;
            try {
                const full = await crud.retrieve("odemeler", odemeId);
                setEditing(full);
                setOdemeComputed(null);
            } catch {
                /* sessiz */
            }
        },
        [isOdemeler],
    );

    const formFields = useMemo(() => entity?.fields || [], [entity]);

    const formTitle = useMemo(() => {
        if (!entity) return "";
        return editing?.id || editIntent || formLoading
            ? `${entity.label} Düzenle`
            : `Yeni ${entity.label}`;
    }, [entity, editing, editIntent, formLoading]);

    const formExtraSection = useMemo(() => {
        if (!formOpen) return null;
        const blocks = [];
        if (evrakConfig) {
            blocks.push(
                <EvrakSection
                    key="evrak"
                    ref={evrakRef}
                    parentId={evrakParentId}
                    relationField={evrakConfig.relationField}
                />,
            );
        }
        if (isVekaletler) {
            blocks.push(
                <VekaletDosyaSection
                    key="vekalet-dosyalar"
                    ref={vekaletDosyaRef}
                    parentId={editing?.id ?? null}
                    onParentUpdated={reloadEditingVekalet}
                />,
            );
            if (!editing?.id) {
                blocks.push(
                    <p
                        key="vekalet-hint"
                        className="text-xs text-amber-200/90 border border-amber-500/20 rounded-md px-3 py-2 bg-amber-500/5"
                    >
                        Dosya yüklemek için önce vekaleti kaydedin; ardından
                        bekleyen dosyalar otomatik yüklenecek.
                    </p>,
                );
            }
        }
        if (isOdemeler) {
            blocks.push(
                <OdemeMasrafSection
                    key="odeme-masraflar"
                    ref={odemeMasrafRef}
                    parentId={editing?.id ?? null}
                    embedded
                    previewOdeme={odemeFormPreview}
                    onTotalsChange={setOdemeComputed}
                />,
            );
        }
        if (!blocks.length) return null;
        return <div className="space-y-4 w-full">{blocks}</div>;
    }, [
        formOpen,
        evrakConfig,
        isVekaletler,
        isOdemeler,
        editing?.id,
        evrakParentId,
        entityKey,
        reloadEditingVekalet,
        odemeFormPreview,
    ]);

    const handleOdemeFormValues = useCallback((vals) => {
        setOdemeFormPreview({
            odeme_miktari: vals?.odeme_miktari,
            anlasma_orani: vals?.anlasma_orani,
        });
    }, []);

    const handleVekaletArsivMode = useCallback(
        (nextArsiv) => {
            setVekaletArsiv(nextArsiv);
            setPage(1);
            const params = new URLSearchParams(location.search);
            if (nextArsiv) params.set("arsiv", "1");
            else params.delete("arsiv");
            const qs = params.toString();
            navigate(
                { pathname: location.pathname, search: qs ? `?${qs}` : "" },
                { replace: true },
            );
        },
        [location.pathname, location.search, navigate],
    );

    const vekaletCellRenderers = useMemo(() => {
        if (!isVekaletler) return null;
        return {
            id: (row) => `#${row.id}`,
            bitis_etiketi: (row) => row.bitis_etiketi || "Süresiz",
            kalan_gun: (row) => {
                if (!row.bitis_tarihi) return "—";
                if (row.kalan_gun == null) return "—";
                return `${row.kalan_gun} gün`;
            },
        };
    }, [isVekaletler]);

    const closeForm = useCallback(() => {
        setFormOpen(false);
        setFormLoading(false);
        setEditIntent(false);
        setEditing(null);
        setOdemeFormPreview(null);
        setOdemeComputed(null);
    }, []);

    const fetchData = useCallback(async () => {
        if (!entity) return;

        setLoading(true);
        setError(null);

        try {
            const params = {
                page,
                page_size: pageSize,
            };

            if (appliedSearch) params.search = appliedSearch;

            for (const [k, v] of Object.entries(appliedFilters)) {
                if (v !== undefined && v !== null && String(v).trim() !== "") {
                    params[k] = v;
                }
            }

            if (sortKey) {
                params.ordering = (sortDir === "desc" ? "-" : "") + sortKey;
            }

            if (isOdemeler) {
                params.yil = odemePeriod.yil;
                if (odemePeriod.ay) params.ay = odemePeriod.ay;
            }

            if (isMuvekkiller) {
                const skipPeriod =
                    appliedFilters.hakedis_bekleyen === "1" ||
                    appliedFilters.acik_dosyali === "1";
                if (!skipPeriod) {
                    params.yil = muvekkilPeriod.yil;
                    if (muvekkilPeriod.ay) params.ay = muvekkilPeriod.ay;
                }
            }

            if (isVekaletler) {
                params.arsiv = vekaletArsiv ? "1" : "0";
            }

            const data = await crud.list(entity.resource, params);

            if (Array.isArray(data)) {
                setRows(data);
                setCount(data.length);
            } else {
                setRows(data.results || []);
                setCount(data.count ?? (data.results || []).length);
            }
        } catch (e) {
            const msg =
                e?.response?.data?.detail ||
                e?.message ||
                "Veriler alınamadı.";

            setError(msg);
            setRows([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    }, [
        entity,
        page,
        pageSize,
        appliedSearch,
        appliedFilters,
        sortKey,
        sortDir,
        isOdemeler,
        odemePeriod,
        isMuvekkiller,
        muvekkilPeriod,
        isSigortaDosya,
        sigortaPeriod,
        isAracilar,
        araciPeriod,
        isVekaletler,
        vekaletArsiv,
    ]);

    useEffect(() => {
        setRows([]);
        setCount(0);
        setPage(1);
        setSearch("");
        setAppliedSearch("");
        setSortKey(null);
        setSortDir("asc");
        setFormOpen(false);
        setEditing(null);
        setDeleting(null);
        setAraciDetail(null);
        const filters = getListFilters(entityKey);
        const params = new URLSearchParams(location.search);
        const fromUrl = {};
        filters.forEach((f) => {
            const v = params.get(f.key);
            if (v !== null && String(v).trim() !== "") fromUrl[f.key] = v;
        });
        setFilterDraft(fromUrl);
        setAppliedFilters(fromUrl);
        if (entityKey === "odemeler") {
            setOdemePeriod({ yil: new Date().getFullYear(), ay: 0 });
        }
        if (entityKey === "muvekkiller") {
            setMuvekkilPeriod({ yil: new Date().getFullYear(), ay: 0 });
            setMuvekkilKpiFilter(null);
        }
        if (entityKey === "sigorta-dosyalari") {
            setSigortaPeriod({ yil: new Date().getFullYear(), ay: 0 });
            setSigortaKpiFilter(null);
        }
        if (entityKey === "aracilar") {
            setAraciPeriod({ yil: new Date().getFullYear(), ay: 0 });
        }
    }, [entityKey, location.search]);

    useEffect(() => {
        if (!isOdemeler) return;
        setPage(1);
    }, [isOdemeler, odemePeriod.yil, odemePeriod.ay]);

    useEffect(() => {
        if (!isMuvekkiller) return;
        setPage(1);
    }, [isMuvekkiller, muvekkilPeriod.yil, muvekkilPeriod.ay]);

    useEffect(() => {
        if (!isSigortaDosya) return;
        setPage(1);
    }, [isSigortaDosya, sigortaPeriod.yil, sigortaPeriod.ay]);

    useEffect(() => {
        if (!isAracilar) return;
        setPage(1);
    }, [isAracilar, araciPeriod.yil, araciPeriod.ay]);

    useEffect(() => {
        if (location.state?.createNew && !formOpen) {
            setEditing(null);
            setFormOpen(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, formOpen, location.pathname, navigate]);

    useEffect(() => {
        const handle = setTimeout(() => {
            const trimmed = search.trim();

            if (trimmed !== appliedSearch) {
                setPage(1);
                setAppliedSearch(trimmed);
            }
        }, 350);

        return () => clearTimeout(handle);
    }, [search, appliedSearch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        const editId = location.state?.editId;
        if (!editId || !rows.length || formOpen) return;
        const row = rows.find((r) => String(r.id) === String(editId));
        if (row) openEdit(row);
        navigate(location.pathname, { replace: true, state: {} });
    }, [rows, location.state, formOpen, location.pathname, navigate, openEdit]);

    const downloadCalcPdf = useCallback(async (row) => {
        setCalcPdfBusyId(row.id);

        try {
            await crud.openFile("deger-kaybi", row.id, "pdf_rapor");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "PDF oluşturulamadı.",
            );
        } finally {
            setCalcPdfBusyId(null);
        }
    }, []);

    const sendDkWhatsApp = useCallback(async (row) => {
        const dosyaId = row.sigorta_dosyasi;
        if (!dosyaId) {
            toast.error("Sigorta dosyası bağlantısı yok.");
            return;
        }
        setDkWaBusyId(row.id);
        try {
            const res = await whatsappMerkez.evrakGonder({
                sigorta_dosyasi_id: dosyaId,
            });
            if (res?.ok) {
                toast.success(res.detail || "WhatsApp hatırlatması kuyruğa alındı.");
            } else {
                toast.warning(res?.detail || "WhatsApp gönderilemedi.");
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "WhatsApp hatırlatması başarısız.",
            );
        } finally {
            setDkWaBusyId(null);
        }
    }, []);

    const submitDkOgrenme = useCallback(
        async (gercekDeger) => {
            if (!dkOgrenmeRow?.id) return;
            setDkOgrenmeBusy(true);
            try {
                const res = await degerKaybiMerkez.ogrenmeDuzelt(
                    dkOgrenmeRow.id,
                    gercekDeger,
                );
                if (res?.ok) {
                    toast.success("Öğrenme kaydı güncellendi — gelecek hesaplar etkilenir.");
                    setDkOgrenmeRow(null);
                } else {
                    toast.error(res?.detail || "Kayıt işlenemedi.");
                }
            } catch (e) {
                toast.error(
                    e?.response?.data?.detail ||
                        e?.message ||
                        "Öğrenme kaydı başarısız.",
                );
            } finally {
                setDkOgrenmeBusy(false);
            }
        },
        [dkOgrenmeRow],
    );

    const degerKaybiCustomActions = useCallback(
        (row) => (
            <DegerKaybiRowActions
                row={row}
                pdfBusy={calcPdfBusyId === row.id}
                waBusy={dkWaBusyId === row.id}
                onPdf={downloadCalcPdf}
                onWhatsApp={sendDkWhatsApp}
                onOgrenme={setDkOgrenmeRow}
            />
        ),
        [calcPdfBusyId, dkWaBusyId, downloadCalcPdf, sendDkWhatsApp],
    );

    const degerKaybiRowTitle = useCallback(
        (row) => (row?.uyari_metin ? String(row.uyari_metin) : undefined),
        [],
    );

    const degerKaybiRowClassName = useCallback((row) => {
        if (row?.hesaplanmamis) {
            return "bg-amber-500/[0.05] hover:bg-amber-500/[0.09]";
        }
        if (row?.uyari_metin) {
            return "bg-rose-500/[0.04] hover:bg-rose-500/[0.08]";
        }
        if (row?.dk_asama === "TAHSILAT") {
            return "bg-emerald-500/[0.05] hover:bg-emerald-500/[0.09]";
        }
        return "";
    }, []);

    const downloadAraciPdf = useCallback(async (row) => {
        setPdfBusyId(row.id);

        try {
            await crud.openFile("aracilar", row.id, "pdf_rapor");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "PDF oluşturulamadı.",
            );
        } finally {
            setPdfBusyId(null);
        }
    }, []);

    const downloadAraciBulkPdf = useCallback(async () => {
        setBulkPdfBusy(true);

        try {
            await crud.openFile("aracilar", "toplu_pdf_rapor");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "Toplu PDF oluşturulamadı.",
            );
        } finally {
            setBulkPdfBusy(false);
        }
    }, []);

    const aracilarCustomActions = useCallback(
        (row) => {
            const actionBusy = pdfBusyId === row.id;

            return (
                <button
                    type="button"
                    data-testid={`araci-pdf-${row.id}`}
                    onClick={() => downloadAraciPdf(row)}
                    disabled={actionBusy}
                    title="Bu aracı için PDF rapor"
                    className="h-8 px-2 rounded-md bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#F3E5AB] transition inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                >
                    {actionBusy ? (
                        <div className="h-3 w-3 rounded-full border-2 border-[#F3E5AB] border-t-transparent animate-spin" />
                    ) : (
                        <FileDown className="h-3.5 w-3.5" />
                    )}
                    <span className="hidden xl:inline">PDF Rapor</span>
                </button>
            );
        },
        [pdfBusyId, downloadAraciPdf],
    );

    const preparePackage = useCallback(
        async (row) => {
            setPreparingId(row.id);

            try {
                const data = await crud.action(
                    "sigorta-basvurulari",
                    row.id,
                    "hazirla_paket",
                );

                const hasZip = Boolean(data?.zip_url || data?.olusturulan_zip);
                const hasDocx = Boolean(data?.docx_url || data?.olusturulan_docx);

                if (hasZip || hasDocx) {
                    toast.success(
                        `Paket hazır! ${hasDocx ? "DOCX " : ""}${
                            hasZip ? "ZIP" : ""
                        } indirme başlıyor.`,
                    );

                    const zipName = `Paket_${row.sigorta_dosyasi_label || row.id}.zip`;
                    const docxName = `Dilekce_${row.sigorta_dosyasi_label || row.id}.docx`;
                    if (hasZip) {
                        setTimeout(() => {
                            crud
                                .downloadFile(
                                    "sigorta-basvurulari",
                                    row.id,
                                    "indir_zip",
                                    zipName,
                                )
                                .catch((err) =>
                                    toast.error(
                                        err?.response?.data?.detail ||
                                            err?.message ||
                                            "ZIP indirilemedi.",
                                    ),
                                );
                        }, 250);
                    }
                    if (hasDocx) {
                        setTimeout(() => {
                            crud
                                .downloadFile(
                                    "sigorta-basvurulari",
                                    row.id,
                                    "indir_docx",
                                    docxName,
                                )
                                .catch((err) =>
                                    toast.error(
                                        err?.response?.data?.detail ||
                                            err?.message ||
                                            "DOCX indirilemedi.",
                                    ),
                                );
                        }, 700);
                    }
                } else {
                    toast.success(
                        `#${row.id} için paket hazırlama başlatıldı.`,
                    );
                }

                fetchData();
                setTimeout(() => fetchData(), 2500);
                setTimeout(() => fetchData(), 6000);
            } catch (e) {
                toast.error(
                    e?.response?.data?.detail ||
                        e?.message ||
                        "Paket hazırlama başarısız.",
                );
            } finally {
                setPreparingId(null);
            }
        },
        [fetchData],
    );

    const prepareMail = useCallback(
        async (row) => {
            setMailBusyId(row.id);
            try {
                const data = await crud.action(
                    "sigorta-basvurulari",
                    row.id,
                    "mail_hazirla",
                );
                openSigortaMailCompose(row, {
                    alicilar: data?.alicilar ?? data?.mail_to,
                    konu: data?.konu ?? data?.mail_subject,
                    mail_plain_text:
                        data?.mail_plain_text ??
                        data?.mail_body ??
                        data?.sablon_html,
                });
                toast.success(
                    "Outlook açıldı — alıcı, konu ve metin aktarıldı. Evrakları siz ekleyin.",
                );
                fetchData();
            } catch (e) {
                toast.error(
                    e?.response?.data?.detail ||
                        e?.message ||
                        "Mail hazırlanamadı.",
                );
            } finally {
                setMailBusyId(null);
            }
        },
        [fetchData],
    );

    const downloadSigortaFile = useCallback(async (row, action, filename) => {
        try {
            await crud.downloadFile(
                "sigorta-basvurulari",
                row.id,
                action,
                filename,
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "Dosya indirilemedi.",
            );
        }
    }, []);

    const sigortaCustomActions = useCallback(
        (row) => {
            const hasDocx = Boolean(row?.olusturulan_docx || row?.docx_url);
            const hasZip = Boolean(row?.olusturulan_zip || row?.zip_url);
            const isPreparing = preparingId === row.id;
            const isMailBusy = mailBusyId === row.id;
            const label = row?.sigorta_dosyasi_label || row?.id;

            return (
                <>
                    <button
                        type="button"
                        data-testid={`dilekce-olustur-${row.id}`}
                        onClick={async () => {
                            setPreparingId(row.id);
                            try {
                                const data = await crud.action(
                                    "sigorta-basvurulari",
                                    row.id,
                                    "dilekce_olustur",
                                );
                                toast.success("Dilekçe oluşturuldu.");
                                if (data?.docx_url || data?.olusturulan_docx) {
                                    setTimeout(() => {
                                        downloadSigortaFile(
                                            row,
                                            "indir_docx",
                                            `Dilekce_${label}.docx`,
                                        );
                                    }, 400);
                                }
                                fetchData();
                            } catch (e) {
                                toast.error(
                                    e?.response?.data?.detail ||
                                        e?.message ||
                                        "Dilekçe oluşturulamadı.",
                                );
                            } finally {
                                setPreparingId(null);
                            }
                        }}
                        disabled={isPreparing}
                        title="Dilekçe oluştur (DOCX)"
                        className="h-8 px-2 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 transition inline-flex items-center gap-1.5 text-xs font-semibold disabled:opacity-50"
                    >
                        {isPreparing ? (
                            <div className="h-3 w-3 rounded-full border-2 border-emerald-300 border-t-transparent animate-spin" />
                        ) : (
                            <FileText className="h-3.5 w-3.5" />
                        )}
                        <span className="hidden xl:inline">Dilekçe</span>
                    </button>

                    {hasDocx && (
                        <button
                            type="button"
                            data-testid={`download-docx-${row.id}`}
                            title="DOCX indir"
                            onClick={() =>
                                downloadSigortaFile(
                                    row,
                                    "indir_docx",
                                    `Dilekce_${label}.docx`,
                                )
                            }
                            className="h-8 px-2 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 transition inline-flex items-center gap-1.5 text-xs font-semibold"
                        >
                            <FileText className="h-3.5 w-3.5" />
                            DOCX
                        </button>
                    )}

                    {hasZip && (
                        <button
                            type="button"
                            data-testid={`download-zip-${row.id}`}
                            title="ZIP indir"
                            onClick={() =>
                                downloadSigortaFile(
                                    row,
                                    "indir_zip",
                                    `Paket_${label}.zip`,
                                )
                            }
                            className="h-8 px-2 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 transition inline-flex items-center gap-1.5 text-xs font-semibold"
                        >
                            <Archive className="h-3.5 w-3.5" />
                            ZIP
                        </button>
                    )}

                    <button
                        type="button"
                        data-testid={`prepare-package-${row.id}`}
                        onClick={() => preparePackage(row)}
                        disabled={isPreparing}
                        title="Paket Hazırla"
                        className="h-8 px-2 rounded-md bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#F3E5AB] transition inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                    >
                        {isPreparing ? (
                            <div className="h-3 w-3 rounded-full border-2 border-[#F3E5AB] border-t-transparent animate-spin" />
                        ) : (
                            <Package className="h-3.5 w-3.5" />
                        )}
                        <span className="hidden xl:inline">Paket Hazırla</span>
                    </button>

                    <button
                        type="button"
                        data-testid={`mail-prepare-${row.id}`}
                        title="Alıcı, konu ve metni e-posta programında aç (evrakları siz eklersiniz)"
                        disabled={isMailBusy}
                        onClick={() => prepareMail(row)}
                        className="h-8 px-2 rounded-md bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 text-sky-300 transition inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                    >
                        {isMailBusy ? (
                            <div className="h-3 w-3 rounded-full border-2 border-sky-300 border-t-transparent animate-spin" />
                        ) : (
                            <Mail className="h-3.5 w-3.5" />
                        )}
                        <span className="hidden xl:inline">Mail Hazırla</span>
                    </button>
                </>
            );
        },
        [preparingId, mailBusyId, preparePackage, prepareMail, downloadSigortaFile],
    );

    const sigortaDosyaCustomActions = useCallback(
        (row) => (
            <a
                href={`mailto:?subject=${encodeURIComponent(
                    `Eksik evrak — ${row.dosya_no || row.id}`,
                )}`}
                title="Eksik evrak maili"
                className="h-8 px-2 rounded-md bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 text-sky-300 transition inline-flex items-center gap-1.5 text-xs"
                onClick={(e) => e.stopPropagation()}
            >
                <Mail className="h-3.5 w-3.5" />
                <span className="hidden xl:inline">Mail</span>
            </a>
        ),
        [],
    );

    const handleMuvekkilKpiFilter = useCallback((filter) => {
        setPage(1);
        setMuvekkilKpiFilter(filter);
        const kpiKeys = [
            "vekalet_durumu",
            "kimlik_tipi",
            "kvkk_bekleyen",
            "portal_eksik",
            "portal_sifreli",
            "email_eksik",
            "whatsapp_eksik",
            "telefon_eksik",
            "iban_eksik",
            "onboarding_eksik",
            "hakedis_bekleyen",
            "acik_dosyali",
            "aracili",
        ];
        if (!filter) {
            setAppliedFilters((prev) => {
                const next = { ...prev };
                kpiKeys.forEach((k) => delete next[k]);
                return next;
            });
            setFilterDraft((prev) => {
                const next = { ...prev };
                kpiKeys.forEach((k) => delete next[k]);
                return next;
            });
            return;
        }
        const patch = { [filter.key]: filter.value };
        setAppliedFilters((prev) => {
            const next = { ...prev };
            kpiKeys.forEach((k) => {
                if (k !== filter.key) delete next[k];
            });
            return { ...next, ...patch };
        });
        setFilterDraft((prev) => {
            const next = { ...prev };
            kpiKeys.forEach((k) => {
                if (k !== filter.key) delete next[k];
            });
            return { ...next, ...patch };
        });
    }, []);

    const handleSigortaKpiFilter = useCallback((filter) => {
        setPage(1);
        setSigortaKpiFilter(filter);
        if (!filter) {
            setAppliedFilters((prev) => {
                const next = { ...prev };
                delete next.dosya_durumu;
                delete next.on_tahkim_sure_durumu;
                return next;
            });
            setFilterDraft((prev) => {
                const next = { ...prev };
                delete next.dosya_durumu;
                delete next.on_tahkim_sure_durumu;
                return next;
            });
            return;
        }
        const patch = { [filter.key]: filter.value };
        setAppliedFilters((prev) => {
            const next = { ...prev, ...patch };
            if (filter.key === "dosya_durumu") delete next.on_tahkim_sure_durumu;
            if (filter.key === "on_tahkim_sure_durumu") delete next.dosya_durumu;
            return next;
        });
        setFilterDraft((prev) => {
            const next = { ...prev, ...patch };
            if (filter.key === "dosya_durumu") delete next.on_tahkim_sure_durumu;
            if (filter.key === "on_tahkim_sure_durumu") delete next.dosya_durumu;
            return next;
        });
    }, []);

    const sigortaDosyaRowClassName = useCallback((row) => {
        const tahkim = String(row?.on_tahkim_sure_durumu || "").toUpperCase();
        if (tahkim === "GECIKTI") {
            return "bg-rose-500/[0.08] hover:bg-rose-500/[0.14]";
        }
        if (tahkim === "YAKLASIYOR" || tahkim === "HAZIR") {
            return "bg-amber-500/[0.07] hover:bg-amber-500/[0.12]";
        }
        const sure = String(row?.sure_durumu || "").toUpperCase();
        if (sure === "GECTI") {
            return "bg-rose-500/[0.06] hover:bg-rose-500/[0.11]";
        }
        if (sure === "SON_GUN" || sure === "YAKLASIYOR") {
            return "bg-amber-500/[0.05] hover:bg-amber-500/[0.10]";
        }
        if (String(row?.dosya_durumu || "").toUpperCase() === "EVRAK_EKSIK") {
            return "bg-sky-500/[0.04] hover:bg-sky-500/[0.08]";
        }
        if (String(row?.dosya_durumu || "").toUpperCase() === "KAPATILDI") {
            return "bg-emerald-500/[0.04] hover:bg-emerald-500/[0.08]";
        }
        return "";
    }, []);

    const sigortaCellRenderers = useMemo(
        () => ({
            muvekkil: (row) => {
                const ad =
                    row.muvekkil_ad_soyad ||
                    row.muvekkil_label ||
                    "—";
                return (
                    <div className="truncate text-slate-100" title={ad}>
                        {ad}
                    </div>
                );
            },
            sigorta_sirketi: (row) => {
                const v =
                    row.sigorta_sirketi_label ||
                    row.sigorta_sirketi ||
                    "—";
                return (
                    <div className="truncate text-slate-200" title={String(v)}>
                        {v}
                    </div>
                );
            },
            basvuru_konusu: (row) => (
                <div className="truncate text-slate-200">
                    {row.basvuru_konusu_display || row.basvuru_konusu || "—"}
                </div>
            ),
            dosya_durumu: (row) => (
                <div className="truncate text-slate-200">
                    {row.dosya_durumu_display || row.dosya_durumu || "—"}
                </div>
            ),
            dosya_durumu: (row) => (
                <div className="truncate text-slate-300 text-[11px]">
                    {row.dosya_durumu_display || row.dosya_durumu || "—"}
                </div>
            ),
            avukat: (row) => (
                <div className="truncate text-slate-300 text-[11px]">
                    {row.avukat_label || "—"}
                </div>
            ),
            son_not_ozeti: (row) => (
                <DosyaNotHucre
                    row={row}
                    onRefresh={fetchData}
                    onDrawerOpenChange={(openId) => {
                        setDosyaNotDrawerDosyaId(openId);
                        if (openId) setSigortaDetay(null);
                    }}
                />
            ),
            yatar_acilacak_mi: (row) => {
                const yatar =
                    row.yatar_acilacak_mi ||
                    String(row.basvuru_konusu || "").toUpperCase() === "YATAR";
                if (!yatar) {
                    return (
                        <span className="text-slate-600 text-[11px]">—</span>
                    );
                }
                return (
                    <span className="inline-flex items-center justify-center rounded-full border border-amber-500/35 bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-200">
                        Yatar
                    </span>
                );
            },
            eksik_evrak_sayisi: (row) => {
                const n = Number(row.eksik_evrak_sayisi || 0);
                if (n <= 0) {
                    return (
                        <span className="text-emerald-400/90 text-[11px]">✓</span>
                    );
                }
                return (
                    <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-rose-500/20 text-rose-200 text-[11px] font-mono">
                        {n}
                    </span>
                );
            },
            kalan_gun: (row) => {
                const n = row.kalan_gun;
                if (n == null || n === "") {
                    return <span className="text-slate-600">—</span>;
                }
                const num = Number(n);
                const tone =
                    num <= 0
                        ? "text-rose-300"
                        : num <= 20
                          ? "text-amber-300"
                          : "text-slate-300";
                return (
                    <span className={`font-mono-jb text-[11px] ${tone}`}>
                        {n}
                    </span>
                );
            },
            tahkim_gecis: (row) => (
                <SigortaTahkimGecisHucre row={row} onRefresh={fetchData} />
            ),
            on_tahkim_hedef_tarihi: (row) => {
                const raw = row.on_tahkim_hedef_tarihi;
                if (!raw) return <span className="text-slate-600 text-[11px]">—</span>;
                const parts = String(raw).slice(0, 10).split("-");
                const fmt =
                    parts.length === 3
                        ? `${parts[2]}.${parts[1]}.${parts[0]}`
                        : raw;
                return (
                    <span className="font-mono-jb text-[11px] text-slate-300" title={String(raw)}>
                        {fmt}
                    </span>
                );
            },
        }),
        [fetchData],
    );

    const openSigortaDetay = useCallback((row, event) => {
        if (!row?.id) return;
        const tr = event?.currentTarget;
        const rect = tr?.getBoundingClientRect?.();
        const anchor = rect
            ? {
                  top: rect.top,
                  left: rect.left,
                  right: rect.right,
                  bottom: rect.bottom,
                  width: rect.width,
                  height: rect.height,
              }
            : null;
        setSigortaDetay({ row, anchor });
    }, []);

    const handleSigortaRowClick = useCallback(
        (row, event) => {
            if (dosyaNotDrawerDosyaId) return;
            if (event?.target?.closest?.("[data-stop-row-click]")) return;
            openSigortaDetay(row, event);
        },
        [openSigortaDetay, dosyaNotDrawerDosyaId],
    );

    const openMuvekkilDetay = useCallback((row, event) => {
        if (!row?.id) return;
        const tr = event?.currentTarget;
        const rect = tr?.getBoundingClientRect?.();
        const anchor = rect
            ? {
                  top: rect.top,
                  left: rect.left,
                  right: rect.right,
                  bottom: rect.bottom,
                  width: rect.width,
                  height: rect.height,
              }
            : null;
        setMuvekkilDetay({ row, anchor });
    }, []);

    const handleMuvekkilRowClick = useCallback(
        (row, event) => {
            openMuvekkilDetay(row, event);
        },
        [openMuvekkilDetay],
    );

    const sigortaRowClassName = useCallback((row) => {
        const durum = String(row?.durum || "").toLowerCase();

        if (durum === "tamamlandi" || durum === "tamamlandı") {
            return "bg-emerald-500/[0.06] hover:bg-emerald-500/[0.10]";
        }

        if (durum === "hazir" || durum === "hazır") {
            return "bg-[#D4AF37]/[0.06] hover:bg-[#D4AF37]/[0.10]";
        }

        return "";
    }, []);

    function onSort(key) {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    }

    function openCreate() {
        setEditIntent(false);
        setFormLoading(false);
        setEditing(null);
        setOdemeFormPreview(null);
        setOdemeComputed(null);
        setFormOpen(true);
    }

    async function openEdit(row) {
        setEditIntent(true);
        setFormLoading(true);
        setEditing(null);
        setFormOpen(true);
        try {
            const full = await crud.retrieve(entity.resource, row.id);
            setEditing(full);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "Kayıt detayı yüklenemedi.",
            );
            closeForm();
        } finally {
            setFormLoading(false);
        }
    }

    function openDetail(row) {
        if (row?.id == null) return;
        navigate(`/e/${entityKey}/${row.id}`);
    }

    async function submitForm(payload, opts = {}) {
        const body = normalizeEntityPayload(entityKey, payload);
        const sendOdemeWhatsapp = Boolean(isOdemeler && body.whatsapp_gonder);
        if (isOdemeler && "whatsapp_gonder" in body) {
            delete body.whatsapp_gonder;
        }
        try {
            let odemeId = editing?.id ?? null;
            let waSonuc = null;

            if (editing?.id) {
                const updated = await crud.update(
                    entity.resource,
                    editing.id,
                    {
                        ...body,
                        ...(sendOdemeWhatsapp ? { whatsapp_gonder: true } : {}),
                    },
                    { force: opts.force },
                );
                odemeId = editing.id;
                waSonuc = updated?.whatsapp_sonuc;
                toast.success(`${entity.label} güncellendi`);
            } else {
                const created = await crud.create(entity.resource, body);
                odemeId = created?.id ?? null;
                toast.success(`${entity.label} oluşturuldu`);
                const evrakFlushId = evrakConfig?.resolveParentId
                    ? evrakConfig.resolveParentId(created)
                    : created?.id;
                if (evrakFlushId && evrakRef.current?.hasQueue?.()) {
                    const { success, failed } =
                        await evrakRef.current.flushQueue(evrakFlushId);
                    if (success > 0) {
                        toast.success(`${success} evrak yüklendi`);
                    }
                    if (failed > 0) {
                        toast.error(
                            `${failed} evrak yüklenemedi — formu tekrar açıp deneyin.`,
                        );
                    }
                }
                if (
                    isVekaletler &&
                    created?.id &&
                    vekaletDosyaRef.current?.hasQueue?.()
                ) {
                    const { success, failed } =
                        await vekaletDosyaRef.current.flushQueue(created.id);
                    if (success > 0) {
                        toast.success(`${success} vekalet dosyası yüklendi`);
                    }
                    if (failed > 0) {
                        toast.error(
                            `${failed} vekalet dosyası yüklenemedi — formu tekrar açıp deneyin.`,
                        );
                    }
                }
                if (
                    isOdemeler &&
                    created?.id &&
                    odemeMasrafRef.current?.hasQueue?.()
                ) {
                    const { success, failed } =
                        await odemeMasrafRef.current.flushQueue(created.id);
                    if (success > 0) {
                        toast.success(`${success} masraf kalemi kaydedildi`);
                    }
                    if (failed > 0) {
                        toast.error(
                            `${failed} masraf kalemi kaydedilemedi — formu tekrar açıp deneyin.`,
                        );
                    }
                }
                if (sendOdemeWhatsapp && odemeId) {
                    waSonuc = await crud.action(
                        "odemeler",
                        odemeId,
                        "whatsapp-bildir",
                    );
                }
            }

            if (waSonuc) {
                if (waSonuc.ok) {
                    toast.success(
                        waSonuc.detail ||
                            "WhatsApp ödeme bildirimi kuyruğa alındı.",
                    );
                } else {
                    toast.warning(
                        waSonuc.detail || "WhatsApp bildirimi gönderilemedi.",
                    );
                }
            }

            closeForm();
            fetchData();
        } catch (e) {
            throw e;
        }
    }

    async function confirmDelete() {
        if (!deleting) return;

        setBusy(true);

        try {
            await crud.remove(entity.resource, deleting.id);
            toast.success(`${entity.label} silindi`);
            setDeleting(null);

            if (rows.length === 1 && page > 1) {
                setPage(page - 1);
            } else {
                fetchData();
            }
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    e?.message ||
                    "Silme işlemi başarısız.",
            );
        } finally {
            setBusy(false);
        }
    }

    async function exportServerCsv() {
        if (!entity) return;
        setExportBusy(true);
        try {
            const params = {};
            if (appliedSearch) params.search = appliedSearch;
            for (const [k, v] of Object.entries(appliedFilters)) {
                if (v !== undefined && v !== null && String(v).trim() !== "") params[k] = v;
            }
            if (sortKey) params.ordering = (sortDir === "desc" ? "-" : "") + sortKey;
            await crud.exportCsv(entity.resource, params);
            toast.success("CSV dışa aktarıldı (filtreli tüm kayıtlar).");
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Dışa aktarma başarısız.");
        } finally {
            setExportBusy(false);
        }
    }

    function exportCsv() {
        if (!rows.length) {
            toast.info("Dışa aktarılacak veri yok.");
            return;
        }

        const keys = Array.from(
            rows.reduce((s, r) => {
                Object.keys(r || {}).forEach((k) => s.add(k));
                return s;
            }, new Set()),
        );

        const escape = (v) => {
            if (v === null || v === undefined) return "";

            const value = typeof v === "object" ? JSON.stringify(v) : String(v);

            return `"${value.replace(/"/g, '""')}"`;
        };

        const csv = [
            keys.join(","),
            ...rows.map((r) => keys.map((k) => escape(r[k])).join(",")),
        ].join("\n");

        const blob = new Blob(["\ufeff" + csv], {
            type: "text/csv;charset=utf-8;",
        });

        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${entity.resource}-${new Date()
            .toISOString()
            .slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    }

    if (!entity) {
        return (
            <div className="p-8">
                <div className="text-slate-300">
                    Bu modül tanımlı değil:{" "}
                    <span className="font-mono-jb text-[#F3E5AB]">
                        {entityKey}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`max-w-[1800px] mx-auto animate-fade-in ${
                embedded
                    ? "px-2 py-2 lg:px-4 lg:py-3"
                    : isSigortaDosya
                      ? "px-3 py-3 lg:px-5 lg:py-4"
                      : "p-6 lg:p-8"
            }`}
        >
            {!embedded && isMuvekkiller && (
                <MuvekkilTakipPanel
                    period={muvekkilPeriod}
                    onPeriodChange={setMuvekkilPeriod}
                    onRefresh={fetchData}
                    listCount={count}
                    activeFilter={muvekkilKpiFilter}
                    onFilterChange={handleMuvekkilKpiFilter}
                />
            )}
            {!embedded && isOdemeler && (
                <OdemeTakipPanel
                    period={odemePeriod}
                    onPeriodChange={setOdemePeriod}
                    onRefresh={fetchData}
                    listCount={count}
                />
            )}
            {!embedded && isSigortaDosya && (
                <SigortaDosyaTakipPanel
                    period={sigortaPeriod}
                    onPeriodChange={setSigortaPeriod}
                    onRefresh={fetchData}
                    listCount={count}
                    activeFilter={sigortaKpiFilter}
                    onFilterChange={handleSigortaKpiFilter}
                />
            )}
            {!embedded && isAracilar && (
                <AraciTakipPanel
                    period={araciPeriod}
                    onPeriodChange={setAraciPeriod}
                    onRefresh={fetchData}
                    listCount={count}
                />
            )}
            {!embedded && isDegerKaybiMod && (
                <DegerKaybiMerkezPanel
                    filterParams={dkFilterParams}
                    activeFilters={appliedFilters}
                    onFilterPatch={handleDkFilterPatch}
                    onRefresh={fetchData}
                    listCount={count}
                />
            )}
            {!embedded && isVekaletler && (
                <VekaletMerkezPanel
                    arsivMode={vekaletArsiv}
                    onArsivModeChange={handleVekaletArsivMode}
                />
            )}
            {!embedded && <EntityMerkezBanner entityKey={entityKey} />}
            <div
                className={`flex flex-col md:flex-row md:items-end md:justify-between gap-4 ${
                    embedded ? "mb-3" : "mb-6"
                }`}
            >
                <div>
                    {!embedded ? (
                        <>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                {entity.category}
                            </div>
                        </>
                    ) : null}

                    <h1
                        className={`font-display tracking-tight text-white mt-1 ${
                            embedded ? "text-lg sm:text-xl" : "text-3xl sm:text-4xl"
                        }`}
                    >
                        {entity.title}
                        {isVekaletler && vekaletArsiv ? " — Arşiv" : ""}
                    </h1>

                    <div className="text-sm text-slate-400 mt-1">
                        Toplam{" "}
                        <span className="text-slate-200 font-mono-jb">
                            {count}
                        </span>{" "}
                        kayıt
                    </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        data-testid="refresh-button"
                        onClick={fetchData}
                        className="h-10 px-3 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 text-sm inline-flex items-center gap-2"
                    >
                        <RefreshCw className="h-4 w-4" />
                        Yenile
                    </button>

                    <button
                        data-testid="export-csv-button"
                        onClick={exportServerCsv}
                        disabled={exportBusy}
                        className="h-10 px-3 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 text-sm inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        <Download className="h-4 w-4" />
                        {exportBusy ? "Aktarılıyor…" : "CSV (tümü)"}
                    </button>
                    <button
                        data-testid="export-page-csv-button"
                        onClick={exportCsv}
                        className="h-10 px-3 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 text-sm inline-flex items-center gap-2"
                    >
                        <FileDown className="h-4 w-4" />
                        CSV (sayfa)
                    </button>

                    {isAracilar && (
                        <button
                            data-testid="bulk-pdf-button"
                            onClick={downloadAraciBulkPdf}
                            disabled={bulkPdfBusy}
                            className="h-10 px-3 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#F3E5AB] text-sm inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {bulkPdfBusy ? (
                                <div className="h-3.5 w-3.5 rounded-full border-2 border-[#F3E5AB] border-t-transparent animate-spin" />
                            ) : (
                                <Files className="h-4 w-4" />
                            )}
                            Toplu PDF Raporu
                        </button>
                    )}

                    {!isReadOnlyCrud && (
                        <button
                            data-testid="create-button"
                            onClick={openCreate}
                            className="btn-gold h-10 px-4 rounded-md text-sm inline-flex items-center gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Yeni {entity.label}
                        </button>
                    )}
                </div>
            </div>

            <div className="mb-4 rounded-lg border border-white/10 bg-[#121A2F] p-3 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />

                    <input
                        data-testid="list-search-input"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                setPage(1);
                                setAppliedSearch(search.trim());
                            }
                        }}
                        placeholder={`${entity.title} içinde ara...`}
                        className="w-full bg-[#0B0F19] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm placeholder:text-slate-600 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none"
                    />
                </div>

                <button
                    data-testid="apply-search-button"
                    onClick={() => {
                        setPage(1);
                        setAppliedSearch(search.trim());
                    }}
                    className="h-9 px-3 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 text-xs inline-flex items-center gap-2"
                >
                    <Filter className="h-3.5 w-3.5" />
                    Uygula
                </button>

                {appliedSearch && (
                    <button
                        onClick={() => {
                            setSearch("");
                            setAppliedSearch("");
                            setPage(1);
                        }}
                        className="h-9 px-3 rounded-md text-xs text-slate-400 hover:text-white"
                    >
                        Temizle
                    </button>
                )}
            </div>

            {supportsBulkUpdate(entityKey) && (
                <BulkActionBar
                    entityKey={entityKey}
                    resource={entity.resource}
                    selectedIds={selectedIds}
                    onClear={() => setSelectedIds([])}
                    onDone={fetchData}
                    busy={bulkBusy}
                    setBusy={setBulkBusy}
                />
            )}

            <div className="mb-3 flex items-center gap-2 text-xs text-slate-400">
                <span>Sayfa boyutu:</span>
                {PAGE_SIZE_OPTIONS.map((n) => (
                    <button
                        key={n}
                        type="button"
                        onClick={() => { setPageSize(n); setPage(1); }}
                        className={`px-2 py-1 rounded-md border ${pageSize === n ? "border-[#D4AF37] text-[#F3E5AB] bg-[#D4AF37]/10" : "border-white/10 hover:bg-white/5"}`}
                    >
                        {n}
                    </button>
                ))}
            </div>

            <ListFiltersPanel
                filters={listFilters}
                values={filterDraft}
                onChange={(key, value) =>
                    setFilterDraft((s) => ({ ...s, [key]: value }))
                }
                onApply={() => {
                    setPage(1);
                    setAppliedFilters({ ...filterDraft });
                }}
                onClear={() => {
                    setFilterDraft({});
                    setAppliedFilters({});
                    setPage(1);
                }}
            />

            {isReadOnlyCrud && (
                <div className="mb-4 px-3 py-2 rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-100 text-sm">
                    Bu modül salt okunurdur; kayıtlar Django admin üzerinden
                    düzenlenir.
                </div>
            )}

            {error && (
                <div className="mb-4 px-3 py-2 rounded-md border border-rose-500/30 bg-rose-500/10 text-rose-300 text-sm">
                    {error}
                </div>
            )}

            <DataTable
                rows={rows}
                fields={formFields.length ? formFields : entity.fields || []}
                listColumns={tableConfig.columns}
                listColumnLabels={tableConfig.labels}
                columnWidths={tableConfig.columnWidths}
                columnDisplayKeys={tableConfig.displayKeys || {}}
                loading={loading}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={onSort}
                onEdit={isReadOnlyCrud ? undefined : openEdit}
                onRowClick={
                    isSigortaDosya
                        ? handleSigortaRowClick
                        : isMuvekkiller
                          ? handleMuvekkilRowClick
                          : isReadOnlyCrud
                            ? openDetail
                            : openEdit
                }
                onDelete={
                    isReadOnlyCrud ? undefined : (row) => setDeleting(row)
                }
                onView={openDetail}
                readOnlyCrud={isReadOnlyCrud}
                page={page}
                pageSize={pageSize}
                selectable={supportsBulkUpdate(entityKey)}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
                total={count}
                onPageChange={setPage}
                cellRenderers={vekaletCellRenderers}
                customActions={
                    isSigortaBasvuru
                        ? sigortaCustomActions
                        : isSigortaDosya
                          ? sigortaDosyaCustomActions
                          : isAracilar
                            ? aracilarCustomActions
                            : isDegerKaybiMod
                              ? degerKaybiCustomActions
                              : null
                }
                rowClassName={
                    isSigortaBasvuru
                        ? sigortaRowClassName
                        : isSigortaDosya
                          ? sigortaDosyaRowClassName
                          : isDegerKaybiMod
                            ? degerKaybiRowClassName
                            : null
                }
                cellRenderers={
                    isSigortaDosya ? sigortaCellRenderers : null
                }
                rowTitle={isDegerKaybiMod ? degerKaybiRowTitle : null}
                actionsColumnClass={
                    isDegerKaybiMod ? "min-w-[6.5rem]" : "w-[7.5rem]"
                }
            />

            <DegerKaybiOgrenmeModal
                open={Boolean(dkOgrenmeRow)}
                row={dkOgrenmeRow}
                onClose={() => setDkOgrenmeRow(null)}
                onSubmit={submitDkOgrenme}
                busy={dkOgrenmeBusy}
            />

            {isEvraklar ? (
                <EvrakFormModal
                    open={formOpen}
                    title={formTitle}
                    initial={editing}
                    onClose={closeForm}
                    onSaved={() => {
                        closeForm();
                        fetchData();
                    }}
                />
            ) : isDegerKaybiMod ? (
                <DegerKaybiFormModal
                    open={formOpen}
                    title={entity.label}
                    initial={editing}
                    onClose={closeForm}
                    onSaved={() => {
                        closeForm();
                        fetchData();
                    }}
                />
            ) : isSigortaBasvuru ? (
                <SigortaBasvuruFormModal
                    open={formOpen}
                    title={formTitle}
                    initial={editing}
                    onClose={closeForm}
                    onSaved={fetchData}
                />
            ) : isSlider ? (
                <SliderFormModal
                    open={formOpen}
                    title={formTitle}
                    initial={editing}
                    onClose={closeForm}
                    onSaved={() => {
                        closeForm();
                        fetchData();
                    }}
                />
            ) : (
                <EntityForm
                    open={formOpen}
                    loading={formLoading}
                    title={formTitle}
                    fields={formFields}
                    initial={editing}
                    resource={entity.resource}
                    schema={entity.schema}
                    formFieldsStrict={entity.formFieldsStrict}
                    onClose={closeForm}
                    onSubmit={submitForm}
                    sampleRow={rows[0] || null}
                    extraSection={formExtraSection}
                    computedOverrides={isOdemeler ? odemeComputed : null}
                    onValuesChange={
                        isOdemeler ? handleOdemeFormValues : undefined
                    }
                />
            )}

            <ConfirmDialog
                open={!!deleting}
                title={`${entity.label} silinsin mi?`}
                message={
                    <div>
                        <span className="text-slate-300">
                            <span className="text-[#F3E5AB]">
                                {deleting?.dosya_no ||
                                    deleting?.ad ||
                                    deleting?.baslik ||
                                    deleting?.fatura_no ||
                                    "Bu kayıt"}
                            </span>
                        </span>{" "}
                        kalıcı olarak silinecek.
                    </div>
                }
                confirmLabel="Evet, Sil"
                busy={busy}
                onCancel={() => setDeleting(null)}
                onConfirm={confirmDelete}
            />

            {araciDetail && (
                <AraciDetailDrawer
                    aracı={araciDetail}
                    onClose={() => setAraciDetail(null)}
                />
            )}

            {isSigortaDosya && (
                <SigortaDosyaDetayDrawer
                    open={Boolean(sigortaDetay)}
                    row={sigortaDetay?.row}
                    anchor={sigortaDetay?.anchor}
                    onClose={() => setSigortaDetay(null)}
                    onEdit={(r) => {
                        setSigortaDetay(null);
                        openEdit(r);
                    }}
                />
            )}

            {isMuvekkiller && (
                <MuvekkilDetayDrawer
                    open={Boolean(muvekkilDetay)}
                    row={muvekkilDetay?.row}
                    anchor={muvekkilDetay?.anchor}
                    onClose={() => setMuvekkilDetay(null)}
                    onEdit={(r) => {
                        setMuvekkilDetay(null);
                        openEdit(r);
                    }}
                />
            )}
        </div>
    );
}